import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageHead } from "@/components/PageHead";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full border-b hairline bg-transparent py-3 text-base tracking-editorial outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-foreground";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your enquiry has been received.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHead
        eyebrow="Contact Us / Enquiries"
        titleLines={["Let's begin", "a project."]}
        intro="Tell us about your project and vision — we'd love to be a part of it becoming a reality."
      />

      <section className="edge pb-28 md:pb-40" data-testid="contact-section">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
          {/* Form */}
          <div className="md:col-span-7">
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-10">
              <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
                <div>
                  <label className="eyebrow mb-3 block">Name *</label>
                  <input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className={field}
                  />
                </div>
                <div>
                  <label className="eyebrow mb-3 block">Email *</label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className={field}
                  />
                </div>
              </div>
              <div>
                <label className="eyebrow mb-3 block">Phone</label>
                <input
                  data-testid="contact-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+44 …"
                  className={field}
                />
              </div>
              <div>
                <label className="eyebrow mb-3 block">Message *</label>
                <textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Tell us about your space…"
                  className={`${field} resize-none`}
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="group inline-flex items-center gap-3 bg-primary px-10 py-4 text-sm uppercase tracking-[0.16em] text-primary-foreground transition-opacity disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Send enquiry"}
                <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
              </button>
            </form>
          </div>

          {/* Details */}
          <div className="md:col-span-4 md:col-start-9">
            <Reveal>
              <div className="hairline pt-6">
                <span className="eyebrow">Contact</span>
                <address className="mt-4 space-y-1 not-italic text-base tracking-editorial">
                  {CONTACT.address.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                  <p className="pt-3">
                    <a href={`mailto:${CONTACT.email}`} className="underline-offset-4 hover:underline">
                      {CONTACT.email}
                    </a>
                  </p>
                  <p>
                    <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="hover:underline">
                      {CONTACT.phone}
                    </a>
                  </p>
                </address>
              </div>

              <div className="mt-10 flex gap-5 hairline pt-6">
                {CONTACT.socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`contact-social-${s.label.toLowerCase()}`}
                    className="text-xs uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground"
                  >
                    {s.label}
                  </a>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="contact-hero-image" className="w-full">
        <img
          src="/assets/enquire/hero.jpg"
          alt="House of David interior"
          className="h-[60vh] w-full object-cover md:h-[80vh]"
        />
      </section>
    </>
  );
}
